import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { Flex } from '@withjoy/joykit';
import type { FC } from 'react';
import React, { useRef } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

function calcFitScale(parentwidth: number, parentheight: number, childwidth: number, childheight: number) {
  const parentRatio = parentwidth / parentheight;
  const childRatio = childwidth / childheight;
  return parentRatio < childRatio ? parentwidth / childwidth : parentheight / childheight;
}

// Similar to page scaler. PageScaler inputs only width, and outputs height+scale.
// This one inputs width+height and outputs only scale.
export const FitScaler: FC<{ children: React.ReactNode; padding?: number }> = ({ children, padding = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const self = ref.current;
    if (!self) return;

    let lastChild: HTMLElement | undefined;
    let lastSizeKey: string | undefined;

    const measure = () => {
      const child = self.firstElementChild as HTMLElement;
      if (!child) return;

      const { clientWidth: selfWidth, clientHeight: selfHeight } = self;
      const { clientWidth: childWidth, clientHeight: childHeight } = child;
      const sizeKey = `${selfWidth}x${selfHeight}:${childWidth}x${childHeight}`;
      if (child === lastChild && sizeKey === lastSizeKey) return;
      lastChild = child;
      lastSizeKey = sizeKey;

      // For square products such as napkins, there ends up being no padding, and feels tight.
      // So we are compensating for that here:
      const isSquare = childWidth === childHeight;
      const adjustment = isSquare ? 0.8 : 1;

      const scale = calcFitScale(Math.max(0, selfWidth - padding * 2), Math.max(0, selfHeight - padding * 2), childWidth, childHeight);
      child.style.position = 'absolute';
      child.style.scale = `${scale * adjustment}`;
    };

    // Scale before first paint. The synchronous first delivery then only catches a size that changed
    // since, and the size guard makes it a no-op otherwise.
    measure();
    const resizeObserver = createSafeResizeObserver(measure, { syncInitialDelivery: true });

    resizeObserver.observe(self);
    return () => resizeObserver.disconnect();
  }, [padding, ref]);

  return (
    <Flex
      ref={ref}
      position="relative"
      width="100%" // Match parent width by convention.
      height="100%" // Match parent height by convention.
      alignItems="center"
      justifyContent="center"
    >
      {children}
    </Flex>
  );
};
